<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import type { RadioGroupRootEmits, RadioGroupRootProps } from 'reka-ui'
import { RadioGroupRoot, useForwardPropsEmits } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cn } from '../../../lib/utils'

/**
 * shadcn-vue `RadioGroup` on Reka's `RadioGroupRoot`, styled as a segmented choice: one `role="radiogroup"`
 * named by whatever the screen gives it (an `aria-label`), holding a row of `RadioGroupItem` segments on the
 * `--input` hairline. A value that is none of its options checks none of them. Controlled whenever the parent
 * passes `modelValue`, so a click never shows a choice the parent has not taken.
 */
const props = defineProps<RadioGroupRootProps & { class?: HTMLAttributes['class'] }>()
const emits = defineEmits<RadioGroupRootEmits>()

const delegatedProps = reactiveOmit(props, 'class')
const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <RadioGroupRoot
    v-slot="slotProps"
    data-slot="radio-group"
    v-bind="forwarded"
    :class="cn('border-input inline-flex flex-wrap items-stretch border', props.class)"
  >
    <slot v-bind="slotProps" />
  </RadioGroupRoot>
</template>
